import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ShieldCheck, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Auth-gated modal that shows whenever the logged-in user has one or more
 * required policies pending re-acceptance (new version published while they
 * were away). The modal is not dismissable — the user MUST accept or sign out.
 */
export default function PolicyAcceptanceModal() {
  const { user, logout } = useAuth();
  const [pending, setPending] = useState([]);
  const [accepted, setAccepted] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const check = useCallback(async () => {
    if (!user) { setPending([]); return; }
    setLoading(true);
    try {
      const r = await api.get("/auth/pending-acceptances");
      setPending(r.data.pending || []);
      setAccepted({});
    } catch (err) {
      // 401 during token refresh is expected; anything else is silent.
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { check(); }, [check]);

  const allChecked = pending.length > 0 && pending.every((p) => accepted[p.slug]);

  const submit = async () => {
    if (!allChecked) return;
    setSubmitting(true);
    try {
      const versions = {};
      pending.forEach((p) => { versions[p.slug] = p.current_version; });
      await api.post("/auth/accept-policies", { accepted_versions: versions });
      toast.success("Thanks — policies accepted");
      setPending([]);
      setAccepted({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record acceptance");
    } finally { setSubmitting(false); }
  };

  if (!user || pending.length === 0) return null;

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-w-lg" data-testid="policy-acceptance-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-secondary mb-1">
            <ShieldCheck className="w-4 h-4" />
            <span className="mono text-[10px] uppercase tracking-widest">Policy update</span>
          </div>
          <DialogTitle className="serif text-2xl">Please review the latest policies</DialogTitle>
          <DialogDescription>
            We&apos;ve updated {pending.length === 1 ? "a policy" : `${pending.length} policies`} since your last visit. Review and accept to continue using DPP.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="h-24 rounded-md bg-muted animate-pulse" />
        ) : (
          <div className="space-y-3 py-2">
            {pending.map((p) => (
              <label
                key={p.slug}
                htmlFor={`accept-${p.slug}`}
                className="flex items-start gap-2.5 rounded-md border border-border p-3 cursor-pointer select-none hover:bg-muted/30 transition-colors"
                data-testid={`policy-accept-row-${p.slug}`}
              >
                <input
                  id={`accept-${p.slug}`}
                  type="checkbox"
                  checked={!!accepted[p.slug]}
                  onChange={(e) => setAccepted((a) => ({ ...a, [p.slug]: e.target.checked }))}
                  className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary/40"
                  data-testid={`policy-accept-checkbox-${p.slug}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{p.title}</span>
                    <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground">v{p.current_version}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    I have read and agree to the latest version of the {p.title}.
                  </div>
                  {p.has_file && (
                    <a
                      href={`${API}/trust/documents/${p.slug}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest text-primary hover:underline mt-1.5"
                      data-testid={`policy-download-${p.slug}`}
                    >
                      <Download className="w-3 h-3" /> Download PDF
                    </a>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => logout()}
            className="rounded-full"
            data-testid="policy-signout-btn"
          >
            Sign out
          </Button>
          <Button
            onClick={submit}
            disabled={!allChecked || submitting}
            className="rounded-full"
            data-testid="policy-accept-btn"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording…</> : "Accept and continue"}
          </Button>
        </DialogFooter>
        <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground text-center mt-2">
          Full library: <Link to="/trust" className="underline hover:text-primary">Trust Center</Link>
        </p>
      </DialogContent>
    </Dialog>
  );
}
